import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { TierPeriod, } from 'features/premium/entities';
import { useAvailableIapSubscriptions } from 'features/premium/hooks';
import useStripePayment from 'features/premium/hooks/useStripePayment';
import { getIapLibSubscriptions, getIapSubscriptionPrices, } from 'features/premium/iap';
import { selectAbExtendTrial, selectShouldShowStripe, } from 'features/premium/selectors';
import { getCancellationOffer, getIntroductoryCancellationOffer, } from 'features/promotions/actions';
import { muteAll, useFetchOld } from 'hooks/useFetch';
import { useAppSelector, useAppStore } from 'store/hooks';
import { selectUserId } from 'reducers/selectors';
import { getDiscountByIdFromSubscription, getIapSubscriptionFromProducts, getPrices, getStripeProduct, } from '../entities';
import { bundleId } from '../../../constants';
const getPlatformDiscountData = (subscriptions, offerId, productId) => {
    const matchedSubscription = subscriptions.find((item) => productId === item.productId);
    if (!matchedSubscription)
        return undefined;
    const discount = offerId
        ? getDiscountByIdFromSubscription(matchedSubscription, offerId)
        : null;
    if (offerId && !discount)
        return undefined;
    const monthlySubscription = matchedSubscription.period === TierPeriod.yearly
        ? subscriptions.find((subscription) => subscription.tier === matchedSubscription.tier &&
            subscription.period === TierPeriod.monthly)
        : null;
    const formattedPrices = getPrices(matchedSubscription, monthlySubscription || null, discount, matchedSubscription.period);
    if (!formattedPrices)
        return undefined;
    return {
        formattedPrices,
        platformDiscount: discount || undefined,
        subscription: matchedSubscription,
    };
};
export const getStripeDiscountData = (result, stripePriceMap) => result.stripeOffers
    .map((stripeOffer) => {
    const matchedSubscription = getStripeProduct(stripePriceMap, stripeOffer.productId);
    if (!matchedSubscription)
        return undefined;
    const discount = {
        type: 'stripe',
        discount: {
            couponId: stripeOffer.couponId,
            price: stripeOffer.discountPrice.toString(),
            numberOfPeriods: stripeOffer.periods,
            currency: result.currency,
        },
    };
    if (!discount)
        return undefined;
    const monthlySubscription = matchedSubscription.period === TierPeriod.yearly
        ? stripePriceMap[TierPeriod.monthly][matchedSubscription.tier]
        : null;
    const formattedPrices = getPrices(matchedSubscription, monthlySubscription || null, discount, matchedSubscription.period);
    return {
        type: 'stripe',
        subscription: matchedSubscription,
        discount,
        formattedPrices: {
            ...formattedPrices,
            percentOff: stripeOffer.discount || formattedPrices?.percentOff,
        },
    };
})
    .filter((data) => data != null);
const useCancellationOffer = (loadOnMount = true) => {
    const [, , , fetchOffer] = useFetchOld(muteAll);
    const subscriptions = useAvailableIapSubscriptions();
    const userId = useAppSelector(selectUserId);
    const abExtendTrial = useAppSelector(selectAbExtendTrial);
    const discountData = useRef(undefined);
    const trialExtensionOffer = useRef(undefined);
    const fairPriceOffersRef = useRef(undefined);
    const skipNextPaymentOfferRef = useRef(undefined);
    const store = useAppStore();
    const { stripePriceMap } = useStripePayment();
    const setOfferRefs = useCallback((result) => {
        skipNextPaymentOfferRef.current = undefined;
        if (result?.stripeOffers && stripePriceMap) {
            discountData.current = getStripeDiscountData(result, stripePriceMap);
        }
        if (result?.fairPriceOffers) {
            fairPriceOffersRef.current = {
                currency: result.currency,
                fairPriceOffers: result.fairPriceOffers,
            };
        }
        if (result && 'skipNextPaymentOffer' in result) {
            const offer = result.skipNextPaymentOffer;
            if (offer) {
                skipNextPaymentOfferRef.current = {
                    currency: result.currency,
                    offer,
                };
            }
        }
    }, [stripePriceMap]);
    const loadDiscountData = useCallback(async () => {
        const state = store.getState();
        const shouldShowStripe = selectShouldShowStripe(state);
        trialExtensionOffer.current = undefined;
        if (Platform.OS === 'ios') {
            const { isEligibleForIntroOffer } = state.iapSubscriptions;
            if (isEligibleForIntroOffer ||
                (shouldShowStripe && !state.user.user.emmaProStatus.usedFreeTrial)) {
                const result = await fetchOffer(getIntroductoryCancellationOffer('ios', bundleId === 'com.emma-app.dev' ? 'dev' : undefined));
                if (shouldShowStripe) {
                    setOfferRefs(result);
                    return;
                }
                if (!result || !result.iosOffers)
                    return;
                const libSubscriptions = await getIapLibSubscriptions(result.iosOffers.map((offer) => offer.productId));
                const subscriptionsFromProducts = getIapSubscriptionFromProducts(result.iosOffers, libSubscriptions)
                    .map((subscription) => getIapSubscriptionPrices(subscription))
                    .filter((sub) => sub !== undefined);
                discountData.current = result.iosOffers
                    .map((iosOffer) => {
                    const platformDiscountData = getPlatformDiscountData([...subscriptions, ...subscriptionsFromProducts], null, iosOffer.productId);
                    if (!platformDiscountData)
                        return undefined;
                    return {
                        subscription: platformDiscountData.subscription,
                        formattedPrices: {
                            ...platformDiscountData.formattedPrices,
                            percentOff: iosOffer.discount ||
                                platformDiscountData.formattedPrices.percentOff,
                        },
                    };
                })
                    .filter((data) => data != null);
            }
            else {
                const { productId } = state.user.user.emmaProStatus;
                if (!productId)
                    return;
                const result = await fetchOffer(getCancellationOffer('ios', abExtendTrial));
                trialExtensionOffer.current = result?.trialExtensionOffer;
                if (shouldShowStripe) {
                    setOfferRefs(result);
                    return;
                }
                if (!result || !result.iosOffers)
                    return;
                discountData.current = result.iosOffers
                    .map((iosOffer) => {
                    const platformDiscountData = getPlatformDiscountData(subscriptions, iosOffer.offerId, productId);
                    if (!platformDiscountData?.platformDiscount ||
                        platformDiscountData.platformDiscount.type !== 'ios')
                        return undefined;
                    return {
                        type: 'ios',
                        subscription: platformDiscountData.subscription,
                        formattedPrices: {
                            ...platformDiscountData.formattedPrices,
                            percentOff: iosOffer.discount ||
                                platformDiscountData.formattedPrices.percentOff,
                        },
                        discount: {
                            type: platformDiscountData.platformDiscount.type,
                            discount: {
                                identifier: iosOffer.offerId,
                                keyIdentifier: iosOffer.keyIdentifier,
                                nonce: iosOffer.nonce,
                                signature: iosOffer.signature,
                                timestamp: iosOffer.timestamp,
                            },
                            applicationUsername: userId.toString(),
                        },
                    };
                })
                    .filter((data) => data != null);
            }
        }
        else if (Platform.OS === 'android') {
            const { productId, active } = state.user.user.emmaProStatus;
            const result = await fetchOffer(productId && active
                ? getCancellationOffer('android', abExtendTrial)
                : getIntroductoryCancellationOffer('android'));
            trialExtensionOffer.current = result?.trialExtensionOffer;
            if (shouldShowStripe) {
                setOfferRefs(result);
                return;
            }
            if (!result || !result.androidOffers)
                return;
            discountData.current = result.androidOffers
                .map((androidOffer) => {
                const platformDiscountData = getPlatformDiscountData(subscriptions, androidOffer.offerId, androidOffer.productId);
                if (!platformDiscountData?.platformDiscount ||
                    platformDiscountData.platformDiscount.type !== 'android')
                    return undefined;
                return {
                    type: 'android',
                    subscription: platformDiscountData.subscription,
                    formattedPrices: {
                        ...platformDiscountData.formattedPrices,
                        percentOff: androidOffer.discount ||
                            platformDiscountData.formattedPrices.percentOff,
                    },
                    discount: {
                        type: platformDiscountData.platformDiscount.type,
                        offer: platformDiscountData.platformDiscount.discount,
                    },
                };
            })
                .filter((data) => data != null);
        }
    }, [abExtendTrial, fetchOffer, setOfferRefs, subscriptions, userId]);
    useEffect(() => {
        if (loadOnMount) {
            loadDiscountData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [isRetrying, setIsRetrying] = useState(false);
    const retry = useCallback(async () => {
        // Return after a max of 3 seconds so the buttons don’t get stuck
        setIsRetrying(true);
        const timeout = new Promise((resolve) => {
            setTimeout(resolve, 3000);
        });
        await Promise.race([timeout, loadDiscountData()]);
        setIsRetrying(false);
    }, [loadDiscountData]);
    return {
        retry,
        isFetching: isRetrying,
        cancellationDiscountDataRef: discountData,
        trialExtensionOfferRef: trialExtensionOffer,
        fairPriceOffersRef,
        skipNextPaymentOfferRef,
    };
};
export default useCancellationOffer;
